/**
 * LandingScreen (S1) — daily home. The status hero is the heart of the app.
 * Props: { ctx, api, tg, nav, state, setState, params } (see App.jsx).
 *
 * Renders one of three states from ctx.today.status:
 *   'none' → hero.wait  "Bugun hali kirmagansiz"  · MainButton "Kirish"  (in)
 *   'in'   → hero.in     "Ishdasiz ✅" + kirish/hozir · MainButton "Chiqish" (out, red)
 *   'out'  → hero.done   "Ish kuni tugadi" + kirish/chiqish/jami · MainButton disabled
 * Secondary "📅 Mening davomatim" → push(HISTORY).
 *
 * Starting a punch: setState({direction}) then push BRANCH (if 2 branches) else
 * LOCATION — both carry the chosen direction forward.
 */
import { useEffect, useState } from 'react';
import Screen from '../components/Screen.jsx';
import Button from '../components/Button.jsx';
import {
  T,
  MONTH_UZ,
  hhmm,
  greetHello,
  greetThanks,
  greetingSub,
} from '../lib/i18n.js';
import { SCREENS } from '../App.jsx';

const UZ_WEEKDAY = [
  'yakshanba', 'dushanba', 'seshanba', 'chorshanba',
  'payshanba', 'juma', 'shanba',
];

function todayLine() {
  const d = new Date();
  const month = (MONTH_UZ[d.getMonth() + 1] || '').toLowerCase();
  return `${d.getDate()}-${month}, ${UZ_WEEKDAY[d.getDay()]}`;
}

/** "HH:MM" clock, current device time (for the live "Hozir" readout). */
function nowHM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Minutes-of-day from an ISO clock (reads the string's own +05:00 offset). */
function minsOfDay(iso) {
  const m = /T(\d{2}):(\d{2})/.exec(iso || '');
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}

/** Rough "Ns MMm" worked-total from check_in/check_out (session has no total). */
function durationHM(inIso, outIso) {
  const a = minsOfDay(inIso);
  const b = minsOfDay(outIso);
  if (a == null || b == null) return '';
  let diff = b - a;
  if (diff < 0) diff += 24 * 60;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return `${h}s ${String(m).padStart(2, '0')}m`;
}

function cssVar(name) {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export default function LandingScreen({ ctx, tg, nav, setState }) {
  const today = ctx.today || { status: 'none' };
  const status = today.status || 'none';
  const branchName = ctx.branches[0]?.name || '';

  // Tick the live "Hozir" clock every 30s while checked in (else it freezes at
  // mount). Only runs in the 'in' state where the readout is shown.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (status !== 'in') return undefined;
    const id = setInterval(() => forceTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [status]);

  // Start a punch: LANDING → (BRANCH if 2+) → LOCATION → LIVE (in-app camera).
  // The browser hand-off is no longer the primary path; it now only fires as
  // the automatic fallback INSIDE LIVE when in-app getUserMedia fails.
  function startPunch(direction) {
    setState({ direction });
    if (ctx.branches.length > 1) {
      tg.haptic('selection');
      nav.push(SCREENS.BRANCH, { direction });
    } else {
      tg.haptic('selection');
      nav.push(SCREENS.LOCATION, { direction, branch_id: ctx.branches[0]?.id });
    }
  }

  // Native MainButton reflects the single next allowed action for this state.
  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    return tg.MainButton.onClick(() => {
      if (status === 'none') startPunch('in');
      else if (status === 'in') startPunch('out');
      // 'out' → disabled, no-op
    });
  });

  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    if (status === 'none') {
      tg.MainButton.setParams({ color: cssVar('--accent') || '#2F6BFF' });
      tg.MainButton.setText(T.btn_checkin);
      tg.MainButton.enable();
    } else if (status === 'in') {
      tg.MainButton.setParams({ color: cssVar('--bad') || '#D6353B', text_color: '#FFFFFF' });
      tg.MainButton.setText(T.btn_checkout);
      tg.MainButton.enable();
    } else {
      // reset the colour so the red 'Chiqish' tint from the 'in' state doesn't
      // linger on the disabled 'Bugun tugadi' button.
      tg.MainButton.setParams({
        color: cssVar('--accent') || '#2F6BFF',
        text_color: cssVar('--accent-ink') || '#FFFFFF',
      });
      tg.MainButton.setText(T.btn_day_done);
      tg.MainButton.disable();
    }
    tg.MainButton.show();
    return () => tg.MainButton.hide();
  }, [tg, status]);

  const greet =
    status === 'out' ? greetThanks(ctx.name) : greetHello(ctx.name);
  const sub =
    status === 'out'
      ? T.greet_day_done
      : status === 'in'
        ? T.greet_day_ongoing
        : greetingSub();

  return (
    <Screen
      footer={
        <>
          {!tg.isTelegram ? (
            status === 'none' ? (
              <Button onClick={() => startPunch('in')}>{T.btn_checkin}</Button>
            ) : status === 'in' ? (
              <Button variant="bad" onClick={() => startPunch('out')}>{T.btn_checkout}</Button>
            ) : (
              <Button variant="secondary" disabled>{T.btn_day_done}</Button>
            )
          ) : null}
          <Button variant="ghost" onClick={() => nav.push(SCREENS.HISTORY)}>
            {T.btn_my_attendance}
          </Button>
        </>
      }
    >
      <div className="greet">
        {greet}
        <div className="sm">{sub}</div>
      </div>

      {status === 'none' ? (
        <div className="hero wait">
          <span className="st-pill wait">{T.status_waiting_pill}</span>
          <div className="st-big">{T.status_not_in_yet}</div>
          {branchName ? (
            <div className="st-branch">📍 {T.label_branch}: {branchName}</div>
          ) : null}
        </div>
      ) : null}

      {status === 'in' ? (
        <div className="hero in">
          <span className="st-pill in">{T.status_in_pill}</span>
          <div className="st-big">{T.status_at_work}</div>
          {branchName ? <div className="st-branch">📍 {branchName}</div> : null}
          <div className="st-times">
            <div>{T.label_checkin}<b>{hhmm(today.check_in) || '—'}</b></div>
            <div>{T.label_now}<b>{nowHM()}</b></div>
          </div>
        </div>
      ) : null}

      {status === 'out' ? (
        <div className="hero done">
          <span className="st-pill done">{T.status_done_pill}</span>
          <div className="st-big">{T.status_day_over}</div>
          <div className="st-times">
            <div>{T.label_checkin}<b>{hhmm(today.check_in) || '—'}</b></div>
            <div>{T.label_checkout}<b>{hhmm(today.check_out) || '—'}</b></div>
            <div>{T.label_total}<b>{durationHM(today.check_in, today.check_out) || '—'}</b></div>
          </div>
        </div>
      ) : null}

      <div className="date">{todayLine()}</div>
    </Screen>
  );
}
